import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { BehaviorSubject } from 'rxjs';
import { COLLECTION_NAMES } from '../../constants/collection.const';
import { User } from '../../models/user.model';
import { AuthService } from '../firebase/auth.service';
import { FirestoreService } from '../firebase/firestore.service';
import { NotificationService } from '../notification.service';
import { UserAddressService } from './user-address.service';
import { UserLikeService } from './user-like.service';

@Injectable({
  providedIn: 'root'
})
export class UserService extends FirestoreService {

  private static isInit = false;
  private static unsub = () => {};

  public currentUser: BehaviorSubject<User> = new BehaviorSubject<User>(null);

  constructor(
    private authService: AuthService,
    private userAddressService: UserAddressService,
    private userLikeService: UserLikeService,
    private notificationService: NotificationService,
    private fns: AngularFireFunctions
  ) {
    super();

    this.setCollectionRef(COLLECTION_NAMES.USER);
  }

  setupUser() {
    if(UserService.isInit) return;
    UserService.isInit = true;
    
    this.authService.isAuthenticated.subscribe( isAuthenticated => {
      if(isAuthenticated) {
        this.setUser();
      } else {
        this.currentUser.next(null);
        UserService.unsub();
        this.userAddressService.unsub();
        this.userLikeService.unsub();
        this.notificationService.unsub();
      }
    });
  }

  private async setUser() {
    const docRef = this.getDocRef(this.authService.currentUser.uid)
    UserService.unsub = this.onSnapshot(docRef, (doc) => {
      const user = doc.data();
      if(user) {
        user.isCompletedRegister = this.isCompletedRegister(user);
        user.hasBankAccount = this.hasBankAccount(user);
        this.currentUser.next(user);
        this.userAddressService.setup(user);
        this.userLikeService.setup(user.uid);
        this.notificationService.setup(user.uid);
      }
    });
  }

  private isCompletedRegister(user: User): boolean {
    // Para cadastros vindo da Apple, não precisa de nome
    if((user.providerId == 'apple.com' || user?.displayName) && user?.cpf_cnpj && user?.email && user?.phone && user?.type) {
      return true;
    }

    return false;
  }

  private hasBankAccount(user: User): boolean {
    if(user?.recipient_id && user?.bank_account?.bank && user?.bank_account?.branch_number && user?.bank_account?.account_number && user?.bank_account?.account_check_digit) {
      return true;
    }

    return false;
  }

  async getCurrentUser() {
    const uid = this.authService?.currentUser?.uid;
    if (!uid) return null;

    const user: User = await this.get(uid);
   
    if(user) {
      user.isCompletedRegister = this.isCompletedRegister(user);
      user.hasBankAccount = this.hasBankAccount(user);
      return user;
    }
    
    return { uid } as User;
  }

  async getUserbyId(uid: string){
    const user: User = await this.get(uid);
    
    if(user) {
      user.isCompletedRegister = this.isCompletedRegister(user);
      user.hasBankAccount = this.hasBankAccount(user);
      return user;
    }
    
    return { uid } as User;
  }

  register(user: User) {
    user.create_in = new Date();

    return this.set(user, user.uid);
  }

  override async update(user: User) {
    return this.set(user, user.uid);
  }

  validateCpfCnpj(cpf_cnpj: string): boolean {
    cpf_cnpj = cpf_cnpj.replace(/[^\d]+/g,'');
    if(cpf_cnpj.length === 11) {
      return this.validateCpf(cpf_cnpj);
    } else if(cpf_cnpj.length === 14) {
      return this.validateCnpj(cpf_cnpj);
    }

    return false;
  }

  private validateCpf(cpf: string): boolean {
    let sum = 0;
    let rest;

    if(cpf === '00000000000') return false;

    for(let i = 1; i <= 9; i++) {
      sum = sum + parseInt(cpf.substring(i-1, i)) * (11 - i);
    }

    rest = (sum * 10) % 11;

    if((rest === 10) || (rest === 11)) rest = 0;
    if(rest !== parseInt(cpf.substring(9, 10)) ) return false;

    sum = 0;
    for(let i = 1; i <= 10; i++) {
      sum = sum + parseInt(cpf.substring(i-1, i)) * (12 - i);
    }

    rest = (sum * 10) % 11;

    if((rest === 10) || (rest === 11)) rest = 0;
    if(rest !== parseInt(cpf.substring(10, 11) ) ) return false;

    return true;
  }

  private validateCnpj(cnpj: string): boolean {
    if(cnpj === '00000000000000') return false;

    if(cnpj.length !== 14) return false;

    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    const digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;

    for(let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if(pos < 2) pos = 9;
    }

    let result = sum % 11 < 2 ? 0 : 11 - sum % 11;

    if(result !== parseInt(digits.charAt(0))) return false;

    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;

    for(let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if(pos < 2) pos = 9;
    }

    result = sum % 11 < 2 ? 0 : 11 - sum % 11;

    if(result !== parseInt(digits.charAt(1))) return false;

    return true;
  }

  async updateUser(uid: string, data: Partial<User>): Promise<void> {
    // Pega o usuário atual
    const user = await this.get(uid);

    if (!user) throw new Error('Usuário não encontrado');

    // Faz merge dos dados antigos com os novos
    const updatedUser = { ...user, ...data };

    // Salva usando o método set existente
    return this.set(updatedUser, uid);
  }

  getSellerReviews(sellerId: string) {
    // Prepara a chamada para a função 'getSellerReviews' criada no backend
    const callable = this.fns.httpsCallable('getSellerReviews');
    // Executa a chamada passando o ID do vendedor e retorna o resultado
    return callable({ sellerId });
  }

}
